import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (e) {
        console.error('Error fetching categories:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  if (loading) {
    return (
      <section className="py-12 md:py-16 bg-white" id="services">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="mb-12">
              <div className="h-7 w-48 bg-gray-200 rounded animate-pulse mb-6" />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="aspect-[4/3] bg-gray-200 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16 bg-white" id="services">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10">
          Browse by category
        </h2>

        {categories.map((category, catIdx) => {
          const isExpanded = expandedCat === catIdx;
          const visibleServices = isExpanded ? category.services : category.services.slice(0, 3);
          const hasMore = category.services.length > 3;

          return (
            <div key={catIdx} className="mb-12 last:mb-0">
              {/* Category Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg md:text-xl font-bold text-gray-900">
                  {category.title}
                  <span className="text-gray-400 text-sm font-normal ml-2">
                    ({category.services.length} services)
                  </span>
                </h3>
                <a
                  href="#"
                  className="text-[#37b34a] hover:text-[#2d9a3e] text-sm font-semibold flex items-center gap-1 no-underline transition-colors duration-200"
                >
                  View All
                  <ArrowRight size={16} />
                </a>
              </div>

              {/* Service Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {visibleServices.map((service, svcIdx) => (
                  <a
                    key={svcIdx}
                    href={`/service/${service.slug}`}
                    className="group cursor-pointer no-underline"
                  >
                    <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                      <img
                        src={service.image}
                        alt={service.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <h4 className="text-white text-lg font-bold">{service.name}</h4>
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Show More / Less */}
              {hasMore && (
                <button
                  onClick={() => setExpandedCat(isExpanded ? null : catIdx)}
                  className="mt-4 text-sm font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors duration-200"
                >
                  {isExpanded ? (
                    <>Show less <ChevronUp size={16} /></>
                  ) : (
                    <>Show all {category.services.length} services <ChevronDown size={16} /></>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ServiceCategories;
